import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Radio,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from "@mantine/core";
import {
  FormCategory,
  ValidityDuration,
  ValidityUnit,
} from "../../../types/formTemplates";
import { useFeatureAccess } from "../../../context/FeatureAccessContext";
import { isClinicalVertical } from "../../../utils/verticalCapabilities";

interface FormSettingsModalProps {
  opened: boolean;
  onClose: () => void;
  category: FormCategory;
  validityDuration: ValidityDuration | null;
  onSave: (next: {
    category: FormCategory;
    validity_duration: ValidityDuration | null;
  }) => void;
}

const UNIT_OPTIONS: { value: ValidityUnit; label: string }[] = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

const DEFAULT_DURATION: ValidityDuration = {
  value: 12,
  unit: "months",
};

const CATEGORY_OPTIONS: ReadonlyArray<{
  value: FormCategory;
  label: string;
  description: string;
}> = [
  {
    value: "per_visit",
    label: "Each appointment needs a fresh one",
    description:
      "Client signs again every visit. Used for consent on the specific treatment they're getting that day.",
  },
  {
    value: "reusable",
    label: "Once, then reused across appointments",
    description:
      "Client fills it out once. We keep it on file and reuse it on future appointments until the information needs to be refreshed.",
  },
  {
    value: "per_visit_clinical",
    label: "Clinical notes from staff during the visit",
    description:
      "Staff fills these out during or after the appointment (SOAP notes).",
  },
  {
    value: "post_appointment",
    label: "Sent after the appointment",
    description:
      "Client fills it out after the visit (e.g. photo upload, symptom check-in). Tied to that specific appointment, never reused, never expires. Sent via the salon's post-appointment messages.",
  },
];

export const FormSettingsModal: React.FC<FormSettingsModalProps> = ({
  opened,
  onClose,
  category,
  validityDuration,
  onSave,
}) => {
  const [draftCategory, setDraftCategory] = useState<FormCategory>(category);
  const [indefinite, setIndefinite] = useState<boolean>(!validityDuration);

  // Clinical (SOAP) category is clinical-vertical only; keep it if already set.
  const { businessVertical } = useFeatureAccess();
  const categoryOptions = isClinicalVertical(businessVertical)
    ? CATEGORY_OPTIONS
    : CATEGORY_OPTIONS.filter(
        (o) =>
          o.value !== "per_visit_clinical" || category === "per_visit_clinical",
      );
  const [value, setValue] = useState<number>(
    validityDuration?.value ?? DEFAULT_DURATION.value,
  );
  const [unit, setUnit] = useState<ValidityUnit>(
    validityDuration?.unit ?? DEFAULT_DURATION.unit,
  );

  // Reset every time the modal opens so the inputs match the saved template
  // state. The opened guard avoids clobbering in-progress edits if the parent
  // re-renders with the same opened=true.
  useEffect(() => {
    if (!opened) return;
    setDraftCategory(category);
    setIndefinite(!validityDuration);
    setValue(validityDuration?.value ?? DEFAULT_DURATION.value);
    setUnit(validityDuration?.unit ?? DEFAULT_DURATION.unit);
  }, [opened, category, validityDuration]);

  const showValidity = draftCategory === "reusable";

  const handleSave = () => {
    // Validity_duration is meaningful ONLY for 'reusable'. For per_visit /
    // per_visit_clinical, the backend rejects non-null validity (the form
    // expires at end-of-day of its linked appointment, not based on a
    // template-level timer), so we force-null on save.
    const nextValidity: ValidityDuration | null = !showValidity
      ? null
      : indefinite
        ? null
        : { value, unit };
    onSave({ category: draftCategory, validity_duration: nextValidity });
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      radius="md"
      title={
        <Title order={4} fw={600}>
          Form Settings
        </Title>
      }
    >
      <Stack gap="lg">
        <Stack gap={6}>
          <Text fw={600} size="sm">
            When does this form get filled out?
          </Text>
          <Text size="xs" c="dimmed">
            Tells us how to handle this form when the client books, cancels, or
            reschedules an appointment.
          </Text>
        </Stack>

        <Radio.Group
          value={draftCategory}
          onChange={(v) => setDraftCategory(v as FormCategory)}
          name="form-category"
        >
          <Stack gap="sm">
            {categoryOptions.map((opt) => (
              <Radio
                key={opt.value}
                value={opt.value}
                label={
                  <Stack gap={2}>
                    <Text fw={500} size="sm">
                      {opt.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {opt.description}
                    </Text>
                  </Stack>
                }
              />
            ))}
          </Stack>
        </Radio.Group>

        {showValidity && (
          <Stack gap="sm">
            <Stack gap={6}>
              <Text fw={600} size="sm">
                How long is all this information good for?
              </Text>
              <Text size="xs" c="dimmed">
                After this period the client fills out a fresh copy on their
                next appointment — not just the signature, the whole form.
              </Text>
            </Stack>

            <Switch
              label="Indefinitely"
              description="When on, the information never expires."
              checked={indefinite}
              onChange={(e) => setIndefinite(e.currentTarget.checked)}
            />

            <Group gap="sm" align="flex-end" wrap="nowrap">
              <NumberInput
                label="For"
                min={1}
                value={value}
                onChange={(v) =>
                  setValue(typeof v === "number" && v > 0 ? v : 1)
                }
                disabled={indefinite}
                w={120}
                allowDecimal={false}
                allowNegative={false}
              />
              <Select
                label="Unit"
                data={UNIT_OPTIONS}
                value={unit}
                onChange={(v) => v && setUnit(v as ValidityUnit)}
                disabled={indefinite}
                allowDeselect={false}
                w={140}
              />
            </Group>
          </Stack>
        )}

        {!showValidity && (
          <Box
            p="sm"
            style={{
              background: "var(--mantine-color-gray-0)",
              border: "1px solid var(--mantine-color-gray-2)",
              borderRadius: 8,
            }}
          >
            <Text size="sm" c="dimmed">
              {draftCategory === "per_visit"
                ? "This form is good through the end of each appointment it's attached to. Any reschedule, cancellation, or service change requires the client to fill it out fresh."
                : "These notes belong to a specific appointment and are not reused. They remain on file as a permanent clinical record."}
            </Text>
          </Box>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button color="var(--brand-teal)" onClick={handleSave}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
